import { useState } from "react";
import { createPerson, type CreatePersonResult } from "../actions";

/**
 * The new-contact form behind the + button.
 *
 * Designed for the situation it is actually used in: standing in a doorway or a
 * corridor, one hand, wanting to be finished in fifteen seconds. So the name
 * field is focused on open, only one field beyond the name is really needed,
 * and there is no scrolling before the save button.
 *
 * ## Why "phone or email" is enforced here rather than left optional
 *
 * A contact with neither cannot be recognised again — the next form submission
 * from that person creates a second record. The message says that in plain
 * language instead of just marking a field required, because the reason is not
 * obvious and the rule would otherwise feel like pedantry.
 *
 * ## The three answers the server can give
 *
 * `created` is the ordinary one. `existing` means the number already belongs to
 * somebody, which is far more often a person who forgot than a genuine second
 * contact — so it offers to open that record rather than reporting an error.
 * `ambiguous` means the phone and the email point at two different people; that
 * genuinely needs a human, and the form says so rather than choosing.
 */

interface Props {
  onClose: () => void;
  onOpenPerson: (personId: string) => void;
}

/** Names the identifier that already existed, so the message is specific. */
const MATCH_LABEL: Record<"phone" | "email" | "external_ref", string> = {
  phone: "מספר הטלפון הזה",
  email: "המייל הזה",
  external_ref: "אחד הפרטים",
};

export function NewPersonForm({ onClose, onOpenPerson }: Props) {
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreatePersonResult | null>(null);

  const canSubmit = fullName.trim().length > 0 && (phone.trim() || email.trim()) && !busy;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setError(null);
    try {
      setResult(await createPerson({ fullName, phone, email, note }));
    } catch (err) {
      // The callable's messages are already written in Hebrew for a user to
      // read; anything else gets a generic line rather than a raw error.
      const message = (err as { message?: string })?.message;
      setError(message && /[֐-׿]/.test(message) ? message : "לא הצלחנו לשמור. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  if (result?.status === "created") {
    return (
      <div className="qa-done">
        <div className="qa-done-mark" aria-hidden="true">
          ✓
        </div>
        <h3>{fullName.trim()} נוסף</h3>
        <p className="faint">מעכשיו נזהה אותו אוטומטית בכל טופס שהוא ממלא.</p>
        <div className="btn-row" style={{ marginTop: 18 }}>
          <button type="button" className="btn" onClick={onClose}>
            סיום
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onOpenPerson(result.personId)}
          >
            פתחו את הכרטיס
          </button>
        </div>
      </div>
    );
  }

  if (result?.status === "existing") {
    return (
      <div className="qa-done">
        <div className="qa-done-mark qa-done-mark--info" aria-hidden="true">
          !
        </div>
        <h3>איש הקשר כבר קיים</h3>
        <p className="faint">
          {MATCH_LABEL[result.matchedBy]} כבר רשום אצלנו. לא פתחנו כרטיס נוסף, כדי שלא תהיה כפילות.
        </p>
        <div className="btn-row" style={{ marginTop: 18 }}>
          <button type="button" className="btn" onClick={() => setResult(null)}>
            חזרה
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onOpenPerson(result.personId)}
          >
            פתחו את הכרטיס הקיים
          </button>
        </div>
      </div>
    );
  }

  if (result?.status === "ambiguous") {
    return (
      <div className="qa-done">
        <div className="qa-done-mark qa-done-mark--warn" aria-hidden="true">
          ?
        </div>
        <h3>צריך שתחליטו</h3>
        <p className="faint">
          הטלפון רשום על איש קשר אחד והמייל על איש קשר אחר. לא נבחר לבד, כדי לא לחבר היסטוריה
          של אדם אחד לאדם אחר. זה יחכה לכם באינבוקס.
        </p>
        <div className="btn-row" style={{ marginTop: 18 }}>
          <button type="button" className="btn btn-primary btn-wide" onClick={onClose}>
            הבנתי
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <label className="form-field">
        <span className="form-label">שם</span>
        <input
          className="form-input"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="שם פרטי ומשפחה"
          autoFocus
          autoComplete="off"
          enterKeyHint="next"
        />
      </label>

      <label className="form-field">
        <span className="form-label">טלפון</span>
        <input
          className="form-input"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="050-0000000"
          // `tel` gives a numeric keypad on a phone, and `dir=ltr` keeps a
          // number from being rendered right-to-left inside an RTL page.
          type="tel"
          dir="ltr"
          autoComplete="off"
          enterKeyHint="next"
        />
      </label>

      <label className="form-field">
        <span className="form-label">מייל</span>
        <input
          className="form-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          type="email"
          dir="ltr"
          autoComplete="off"
          enterKeyHint="next"
        />
      </label>

      <label className="form-field">
        <span className="form-label">
          הערה <span className="faint">(לא חובה)</span>
        </span>
        <textarea
          className="form-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="איך הכרתם? במה הוא מעורב?"
          rows={2}
        />
      </label>

      <p className="form-hint">
        צריך טלפון או מייל. בלי זה לא נזהה אותו כשימלא טופס בפעם הבאה, וייפתח לו כרטיס
        נוסף.
      </p>

      {error && <p className="form-error">{error}</p>}

      <div className="btn-row" style={{ marginTop: 4 }}>
        <button type="button" className="btn" onClick={onClose} disabled={busy}>
          ביטול
        </button>
        <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
          {busy ? "שומרים…" : "שמרו"}
        </button>
      </div>
    </form>
  );
}
